import type { SelectHTMLAttributes } from "react";

type Option = { value: string; label: string };

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> & {
  label: string;
  options: Option[];
  placeholder?: string;
  hint?: string;
  error?: string | null;
};

let counter = 0;
const nextId = () => `s${++counter}`;

export default function Select({
  label,
  options,
  placeholder,
  hint,
  error,
  id,
  ...rest
}: Props) {
  const sid = id ?? nextId();
  const hasError = Boolean(error);
  return (
    <label
      htmlFor={sid}
      data-field-error={hasError ? "true" : undefined}
      style={{ display: "block", marginBottom: "1rem" }}
    >
      <div
        className="eyebrow"
        style={{
          color: hasError ? "var(--c-coral)" : "var(--c-palm)",
          fontSize: "0.7rem",
          marginBottom: "0.4rem",
        }}
      >
        {label}
        {hasError && (
          <span aria-hidden="true" style={{ marginLeft: 6 }}>
            *
          </span>
        )}
      </div>
      <select
        id={sid}
        aria-invalid={hasError || undefined}
        {...rest}
        style={{
          width: "100%",
          padding: "0.85rem 1rem",
          borderRadius: "var(--r-sm)",
          border: hasError
            ? "1.5px solid var(--c-coral)"
            : "1px solid var(--c-rule)",
          background: "#fff",
          color: "var(--c-ink)",
          fontSize: "1rem",
          appearance: "none",
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23123F38' d='M0 0l6 8 6-8z'/></svg>\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 1rem center",
          paddingRight: "2.5rem",
          ...(rest.style ?? {}),
        }}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hasError ? (
        <div
          role="alert"
          style={{
            fontSize: "0.8rem",
            color: "var(--c-coral)",
            marginTop: "0.35rem",
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      ) : (
        hint && (
          <div
            style={{
              fontSize: "0.8rem",
              color: "var(--c-palm)",
              opacity: 0.7,
              marginTop: "0.35rem",
            }}
          >
            {hint}
          </div>
        )
      )}
    </label>
  );
}
